// two-fa.service.ts
import { Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { TwoFaCodeDialogComponent } from 'src/app/components/pop-up-component/two-fa-code-dialog/two-fa-code-dialog.component'
import { TwoFaConfirmationComponent } from 'src/app/components/pop-up-component/two-fa-confirmation/two-fa-confirmation.component'
import { AlertService } from './alert.service'
import { UserService } from './user.service'

@Injectable({
  providedIn: 'root',
})
export class TwoFaService {
  public user = this.userService.user

  constructor(
    private userService: UserService,
    private alertService: AlertService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  public async handleEnable2FA(): Promise<boolean> {
    try {
      const confirmEnable = await this.showConfirmationDialog(
        'two_fa.enable.title',
        'two_fa.enable.message'
      )
      if (!confirmEnable) return false

      const phoneNumber = this.user?.get('phone')
      if (!phoneNumber) {
        throw new Error(this.translate.instant('two_fa.phone_required'))
      }

      const confirmSms = await this.showConfirmationDialog(
        'two_fa.sms_confirmation.title',
        this.translate.instant('two_fa.sms_confirmation.message', { phoneNumber })
      )
      if (!confirmSms) return false

      await this.userService.web_sendSmsOTP()

      const verificationSuccess = await this.handleCodeVerification(
        'enable',
        'two_fa.enable.success'
      )

      if (verificationSuccess) {
        this.router.navigate(['/auth/login'])
      }

      return true
    } catch (error) {
      this.handle2FAError(error)
      throw error
    }
  }

  public async handleDisable2FA(onSuccess: VoidFunction): Promise<boolean> {
    try {
      const confirmDisable = await this.showConfirmationDialog(
        'two_fa.disable.title',
        'two_fa.disable.message'
      )
      if (!confirmDisable) return false

      const phoneNumber =
        this.user?.get('phone') || this.translate.instant('two_fa.phone_required')

      const confirmSms = await this.showConfirmationDialog(
        'two_fa.sms_confirmation.title',
        this.translate.instant('two_fa.sms_confirmation.message', { phoneNumber })
      )
      if (!confirmSms) return false

      await this.userService.web_sendSmsOTP()

      const verificationSuccess = await this.handleCodeVerification(
        'disable',
        'two_fa.disable.success'
      )

      if (verificationSuccess) {
        onSuccess()
      }

      return true
    } catch (error) {
      this.handle2FAError(error)
      throw error
    }
  }

  private async handleCodeVerification(
    action: 'enable' | 'disable',
    successKey: string
  ): Promise<boolean> {
    const codeDialogRef = this.dialog.open(TwoFaCodeDialogComponent, {
      disableClose: true,
      width: '400px',
      data: {
        title: this.translate.instant(`two_fa.${action}.code_title`),
        onSubmit: async (code: string) => {
          try {
            codeDialogRef.componentInstance.isSubmitting = true
            codeDialogRef.componentInstance.errorMessage = ''

            if (action === 'enable') {
              await this.userService.web_enable2FA({ otp: code })
            } else {
              await this.userService.web_disable2FA({ otp: code })
            }

            this.alertService.success(this.translate.instant(successKey))
            codeDialogRef.close(true)
            return true
          } catch (error: any) {
            codeDialogRef.componentInstance.errorMessage =
              this.translate.instant('two_fa.code.invalid')
            throw error
          } finally {
            codeDialogRef.componentInstance.isSubmitting = false
          }
        },
      },
    })

    return await codeDialogRef.afterClosed().toPromise()
  }

  public showConfirmationDialog(title: string, message: string): Promise<boolean> {
    return this.dialog
      .open(TwoFaConfirmationComponent, {
        data: { title, message },
        width: '40vw',
      })
      .afterClosed()
      .toPromise()
  }

  public handle2FAError(error: any): void {
    if (error.code === 'CANNOT_DISABLE_2FA_OTHER_DEVICES_ACTIVES') {
      this.alertService.error(
        this.translate.instant('two_fa.disable_error.devices_active')
      )
    } else {
      this.alertService.error(error)
    }
  }
}
